import { typographyTokens } from '../../../../theme/typographyTokens';
// TaskDependencyMapping.tsx (parent)
import React, { useState } from 'react';
import { Paper, Typography, Box, TextField, Button, useTheme, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TaskDependencyMappingTable from './TaskDependencyMappingTable';
import { useGetAllSEPIDsQuery } from '../../../../../frontend/services/sepSlice';
import { useLazyGetTaskByIdQuery, useGetTasksWithSEPIDQuery } from '../../../../../frontend/services/tasksSlice/tasksSlice';

interface SepOption { id: number; name: string }
interface TaskOption { id: number; name: string }

const TaskDependencyMapping = () => {
  const theme = useTheme();
  const [selectedSep, setSelectedSep] = useState<SepOption | null>(null);
  const [selectedTask, setSelectedTask] = useState<TaskOption | null>(null);
  const [taskIdInput, setTaskIdInput] = useState<string>('');
  const [searched, setSearched] = useState(false);
  const [sepID, setSepID] = useState<number | null>(null);
  const [taskIdFilter, setTaskIdFilter] = useState<number | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const { data: sepIDs, error, isLoading } = useGetAllSEPIDsQuery();
  const [getTaskById, { isLoading: isLoadingTask }] = useLazyGetTaskByIdQuery();
  
  // Fetch tasks for selected SEP
  const { data: sepTasks, isLoading: isLoadingTasks } = useGetTasksWithSEPIDQuery(
    selectedSep?.id ?? 0,
    { skip: !selectedSep?.id }
  );
  
  // Map API response to SEP options for Autocomplete
  const sepOptions: SepOption[] = Array.isArray(sepIDs)
    ? sepIDs.map((sep: any) => ({ id: sep.id, name: sep.name }))
    : [];

  // Map tasks for selected SEP to Task options
  const taskOptions: TaskOption[] = Array.isArray(sepTasks)
    ? sepTasks.map((task: any) => ({ id: task.id, name: task.name }))
    : [];
  

  const handleSearch = async () => {
    setSearchError(null);
    const taskIdFromDropdown = selectedTask ? selectedTask.id : null;
    const taskIdFromInput = taskIdInput ? parseInt(taskIdInput, 10) : null;
    
    if (selectedSep && selectedSep.id) {
      // SEP is selected - use task from dropdown
      setSepID(selectedSep.id);
      setTaskIdFilter(taskIdFromDropdown);
      setSearched(true);
    } else if (taskIdFromInput) {
      // Only task ID entered - fetch task to get its SEP ID
      try {
        const taskResult = await getTaskById(taskIdFromInput).unwrap();
        if (taskResult && taskResult.sepID) {
          setSepID(taskResult.sepID);
          setTaskIdFilter(taskIdFromInput);
          setSearched(true);
        } else {
          setSearchError('Task not found or has no associated SEP.');
          setSearched(false);
          setSepID(null);
          setTaskIdFilter(null);
        }
      } catch (err) {
        setSearchError('Task not found. Please check the Task ID.');
        setSearched(false);
        setSepID(null);
        setTaskIdFilter(null);
      }
    } else {
      setSearchError('Please select a SEP or enter a Task ID.');
      setSearched(false);
      setSepID(null);
      setTaskIdFilter(null);
    }
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 2 }}>
        <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, mb: 2 }}>
          Get SEP Task Dependency Mapping
        </Typography>
        {isLoading ? (
          <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.primary.main, mt: 2 }}>
            Loading SEP list...
          </Typography>
        ) : error ? (
          <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.error.main, mt: 2 }}>
            Failed to load SEP list. Please try again later.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={sepOptions}
              getOptionLabel={(option: SepOption) => `${option.id} - ${option.name}`}
              value={selectedSep}
              sx={{ width: '100%' }}
              onChange={(_event: any, value: SepOption | null) => {
                setSelectedSep(value);
                setSelectedTask(null);
                setTaskIdInput('');
              }}
              isOptionEqualToValue={(option: SepOption, value: SepOption) => option.id === value.id}
              renderInput={(params: any) => (
                <TextField {...params} label="Select SEP" variant="outlined" margin="normal" sx={{ fontSize: typographyTokens.fontSizes.body }} />
              )}
            />
            {selectedSep ? (
              <Autocomplete
                options={taskOptions}
                getOptionLabel={(option: TaskOption) => `${option.id} - ${option.name}`}
                value={selectedTask}
                loading={isLoadingTasks}
                sx={{ width: '100%' }}
                onChange={(_event: any, value: TaskOption | null) => setSelectedTask(value)}
                isOptionEqualToValue={(option: TaskOption, value: TaskOption) => option.id === value.id}
                renderInput={(params: any) => (
                  <TextField 
                    {...params} 
                    label="Select Task (Optional)" 
                    variant="outlined" 
                    margin="normal" 
                    sx={{ fontSize: typographyTokens.fontSizes.body }}
                    helperText="Filter by specific Task from this SEP"
                  />
                )}
              />
            ) : (
              <TextField
                label="Task ID (Optional)"
                variant="outlined"
                margin="normal"
                type="number"
                value={taskIdInput}
                onChange={(e) => setTaskIdInput(e.target.value)}
                sx={{ width: '100%' }}
                placeholder="Enter Task ID"
                helperText="Enter Task ID "
              />
            )}
            {searchError && (
              <Typography sx={{ color: theme.palette.error.main, fontSize: typographyTokens.fontSizes.body }}>
                {searchError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, justifyContent: 'flex-end', width: '100%' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  textTransform: 'none',
                }}
                onClick={handleSearch}
                disabled={isLoadingTask}
              >
                {isLoadingTask ? <CircularProgress size={20} color="inherit" /> : 'SEARCH'}
              </Button>
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setSelectedSep(null);
                  setSelectedTask(null);
                  setTaskIdInput('');
                  setSearched(false);
                  setSepID(null);
                  setTaskIdFilter(null);
                  setSearchError(null);
                }}
              >
                CLEAR
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      {searched && sepID && <TaskDependencyMappingTable sepID={sepID} taskIdFilter={taskIdFilter} />}
    </Box>
  );
};

export default TaskDependencyMapping;

